import { animate, AnimationTriggerMetadata, keyframes, query, stagger, state, style, transition, trigger } from '@angular/animations';

export const DEFAULT_TIMING = '300ms ease-in-out';
export const SLOW_TIMING = '500ms ease-in-out';
export const SLOWEST_TIMING = '1s ease-in-out';
export const EASING_ANIMATION = 'cubic-bezier(0.215, 0.61, 0.355, 1)';

export const fadeInOut = trigger('fadeInOut', [
  transition(':enter', [style({ opacity: 0 }), animate(DEFAULT_TIMING, style({ opacity: 1 }))]),
  transition(':leave', [style({ opacity: 1 }), animate(DEFAULT_TIMING, style({ opacity: 0 }))]),
]);

export const fadeIn =
  // trigger name for attaching this animation to an element using the [@triggerName] syntax
  trigger('fadeIn', [
    // route 'enter' transition
    transition(':enter', [
      // css styles at start of transition
      style({ opacity: 0 }),

      // animation and styles at end of transition
      animate(500, style({ opacity: 1 })),
    ]),
  ]);

export const fadeOut =
  // trigger name for attaching this animation to an element using the [@triggerName] syntax
  trigger('fadeOut', [
    // route 'enter' transition
    transition(':leave', [
      // css styles at start of transition
      style({ opacity: 1 }),

      // animation and styles at end of transition
      animate(500, style({ opacity: 0 })),
    ]),

    transition('*=>leave', [
      // css styles at start of transition
      style({ opacity: 1 }),

      // animation and styles at end of transition
      animate(500, style({ opacity: 0, display: 'none' })),
    ]),
  ]);

export const collapseExpand = trigger('collapseExpand', [
  transition(':enter', [
    style({ opacity: 0, height: '0px', overflow: 'hidden' }),
    animate(DEFAULT_TIMING, style({ opacity: 1, height: '*' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, height: '*', overflow: 'hidden' }),
    animate(DEFAULT_TIMING, style({ opacity: 0, height: '0px', overflow: 'hidden' })),
  ]),
]);

export const collapseExpandSlow = trigger('collapseExpandSlow', [
  state('close', style({ opacity: 0, height: '0px', overflow: 'hidden' })),
  state('open', style({ opacity: 1, height: '*' })),
  transition('close <=> open', animate(SLOW_TIMING)),
]);

export const collapseExpandCustomTiming = (timeIn: number, timeExit: number) =>
  trigger('collapseExpandCustomTiming', [
    transition(':enter', [
      style({ height: '0px', minHeight: '0' }),
      animate(`${timeIn}ms ${timeIn}ms ease-in-out`, style({ height: '*' })),
    ]),
    transition(':leave', [
      style({ height: '*', maxHeight: '*', opacity: 1 }),
      animate(`${timeExit}ms`, style({ height: '0px', minHeight: '0', maxHeight: '0', opacity: 0 })),
    ]),
  ]);

export const collapseExpandHeight = trigger('collapseExpandHeight', [
  transition(':enter', [
    style({ opacity: 0, height: '0px', minHeight: '0' }),
    animate(DEFAULT_TIMING, style({ opacity: 1, height: '*' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, height: '*' }),
    animate(DEFAULT_TIMING, style({ opacity: 0, height: '0px', minHeight: '0' })),
  ]),
]);

export const collapseExpandWidth = trigger('collapseExpandWidth', [
  transition(':enter', [
    style({ opacity: 0, width: '0px', minWidth: '0', 'flex-grow': 0, transform: 'translateX(-100%)' }),
    animate(DEFAULT_TIMING, style({ opacity: 1, width: '*', 'flex-grow': 1, transform: 'translateX(0%)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, width: '*', 'flex-grow': 1, transform: 'translateX(0%)' }),
    animate(
      DEFAULT_TIMING,
      style({ opacity: 0, width: '0px', minWidth: '0', 'flex-grow': 0, transform: 'translateX(-100%)' }),
    ),
  ]),
]);

export const slideDown = trigger('slideDown', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(-10%)' }),
    animate(SLOW_TIMING, style({ opacity: 1, transform: 'translateY(0)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateY(0)' }),
    animate(SLOW_TIMING, style({ opacity: 0, transform: 'translateY(-10%)' })),
  ]),
]);

export const slideUp = trigger('slideUp', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(10%)' }),
    animate(SLOW_TIMING, style({ opacity: 1, transform: 'translateY(0)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateY(0)' }),
    animate(SLOW_TIMING, style({ opacity: 0, transform: 'translateY(10%)' })),
  ]),
]);

export const slideLeft = trigger('slideLeft', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateX(-10%)' }),
    animate(SLOW_TIMING, style({ opacity: 1, transform: 'translateX(0)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateX(0)' }),
    animate(SLOW_TIMING, style({ opacity: 0, transform: 'translateX(-10%)' })),
  ]),
]);

export const slideRight = trigger('slideRight', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateX(10%)' }),
    animate(SLOW_TIMING, style({ opacity: 1, transform: 'translateX(0)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateX(0)' }),
    animate(SLOW_TIMING, style({ opacity: 0, transform: 'translateX(10%)' })),
  ]),
]);

export const bounceInOut = trigger('bounceInOut', [
  transition(':enter', [
    animate(
      SLOW_TIMING,
      keyframes([
        style({ transform: 'scale3d(0.3, 0.3, 0.3)', easing: EASING_ANIMATION, offset: 0 }),
        style({ transform: 'scale3d(1.1, 1.1, 1.1)', easing: EASING_ANIMATION, offset: 0.2 }),
        style({ transform: 'scale3d(0.9, 0.9, 0.9)', easing: EASING_ANIMATION, offset: 0.4 }),
        style({ transform: 'scale3d(1.03, 1.03, 1.03)', easing: EASING_ANIMATION, offset: 0.6 }),
        style({ transform: 'scale3d(0.97, 0.97, 0.97)', easing: EASING_ANIMATION, offset: 0.8 }),
        style({ transform: 'scale3d(1, 1, 1)', easing: EASING_ANIMATION, offset: 1 }),
      ]),
    ),
  ]),
  transition(':leave', [
    animate(
      DEFAULT_TIMING,
      keyframes([
        style({ opacity: 1, transform: 'scale3d(1, 1, 1)', easing: EASING_ANIMATION, offset: 0 }),
        style({ opacity: 1, transform: 'scale3d(1.1, 1.1, 1.1)', easing: EASING_ANIMATION, offset: 0.3 }),
        style({ opacity: 0, transform: 'scale3d(0.1, 0.1, 0.1)', easing: EASING_ANIMATION, offset: 1 }),
      ]),
    ),
  ]),
]);

export const pulse = trigger('pulse', [
  transition(
    'false => true',
    animate(
      SLOW_TIMING,
      keyframes([
        style({ transform: 'scale3d(1, 1, 1)', easing: EASING_ANIMATION, offset: 0 }),
        style({ transform: 'scale3d(1.05, 1.05, 1.05)', easing: EASING_ANIMATION, offset: 0.5 }),
        style({ transform: 'scale3d(1, 1, 1)', easing: EASING_ANIMATION, offset: 1 }),
      ]),
    ),
  ),
]);

export const headShake = trigger('headShake', [
  transition(
    'false => true',
    animate(
      SLOWEST_TIMING,
      keyframes([
        style({ transform: 'translateX(0)', offset: 0 }),
        style({ transform: 'translateX(-6px) rotateY(-9deg)', offset: 0.1 }),
        style({ transform: 'translateX(5px) rotateY(7deg)', offset: 0.2 }),
        style({ transform: 'translateX(-3px) rotateY(-5deg)', offset: 0.3 }),
        style({ transform: 'translateX(2px) rotateY(3deg)', offset: 0.4 }),
        style({ transform: 'translateX(0)', offset: 0.6 }),
      ]),
    ),
  ),
]);

export const OpenCloseAnimation: AnimationTriggerMetadata = trigger('openClose', [
  state('true', style({ height: '*', opacity: 1, transform: 'translateY(0px)' })),
  state(
    'false',
    style({
      height: '0px',
      opacity: 0,
      transform: 'translateY(-15px)',
    }),
  ),
  transition('false <=> true', [animate(DEFAULT_TIMING)]),
]);

export const listAnimation = trigger('listAnimation', [
  transition('* <=> *', [
    query(':enter',
      [style({ opacity: 0 }), stagger('60ms', animate('600ms ease-out', style({ opacity: 1 })))],
      { optional: true }
    )
  ])
]);
